// CleverTap is loaded via script tag in public/index.html
// We use window.clevertap directly - no npm package needed

// 6 Custom Event Names
export const KYC_STATUS_PENDING = 'KYC_STATUS_PENDING';
export const KYC_STATUS_ACCEPTED = 'KYC_STATUS_ACCEPTED';
export const KYC_STATUS_SUSPENDED = 'KYC_STATUS_SUSPENDED';
export const USER_TYPE_BUYER = 'USER_TYPE_BUYER';
export const USER_TYPE_SELLER = 'USER_TYPE_SELLER';
export const USER_TYPE_TRADER = 'USER_TYPE_TRADER';

// Map KYC status values to event names
const KYC_EVENT_MAP = {
  PENDING: KYC_STATUS_PENDING,
  KYC_PENDING: KYC_STATUS_PENDING,
  ACCEPT: KYC_STATUS_ACCEPTED,
  ACCEPTED: KYC_STATUS_ACCEPTED,
  KYC_ACCEPTED: KYC_STATUS_ACCEPTED,
  SUSPENDED: KYC_STATUS_SUSPENDED,
  REJECTED: KYC_STATUS_SUSPENDED,
  KYC_REJECTED: KYC_STATUS_SUSPENDED,
};

// Map User type values to event names
const USER_TYPE_EVENT_MAP = {
  BUYER: USER_TYPE_BUYER,
  SELLER: USER_TYPE_SELLER,
  TRADER: USER_TYPE_TRADER,
};

/**
 * Get CleverTap instance from window
 */
const getClevertap = () => {
  if (typeof window !== 'undefined' && window.clevertap) {
    return window.clevertap;
  }
  return null;
};

/**
 * Build vendor details payload for CleverTap event
 */
const buildVendorPayload = (vendor, extraProps = {}) => {
  const callingCode = vendor?.callingCode?.callingCode || vendor?.callingCodeId || '91';
  const phone = vendor?.phone
    ? `+${String(callingCode).replace(/\+/g, '')}${vendor.phone}`
    : '';

  return {
    platform: 'WEB',
    vendor_id: vendor?.id ? String(vendor.id) : 'N/A', // Cast to string to prevent CleverTap type mismatch drops
    name: `${vendor?.firstName || ''} ${vendor?.lastName || ''}`.trim() || 'N/A',
    email: vendor?.email || 'N/A',
    phone: phone || vendor?.phone || 'N/A',
    business_name: vendor?.tradeName || vendor?.entityName || 'N/A',
    ...extraProps,
  };
};

/**
 * Push a CleverTap custom event for KYC status change.
 * Event names: KYC_STATUS_PENDING, KYC_STATUS_ACCEPTED, KYC_STATUS_SUSPENDED
 */
export const trackKycStatusChange = (vendor, previousStatus, newStatus) => {
  const ct = getClevertap();
  if (!ct) {
    console.warn('[CleverTap] SDK not loaded. Event not sent.');
    return;
  }

  const statusKey = String(newStatus || '')
    .trim()
    .toUpperCase()
    .replace(/^KYC_/, '');
  const eventName =
    KYC_EVENT_MAP[statusKey] ||
    KYC_EVENT_MAP[String(newStatus || '').toUpperCase()];

  if (!eventName) {
    console.warn(`[CleverTap] Unknown KYC status: ${newStatus}`);
    return;
  }

  const eventData = buildVendorPayload(vendor, {
    previous_status: previousStatus || 'N/A',
    new_status: newStatus || 'N/A',
  });

  ct.event.push(eventName, eventData);
};

/**
 * Push a CleverTap custom event for User type/role change.
 * Event names: USER_TYPE_BUYER, USER_TYPE_SELLER, USER_TYPE_TRADER
 */
export const trackUserTypeChange = (vendor, previousType, newType) => {
  const ct = getClevertap();
  if (!ct) {
    console.warn('[CleverTap] SDK not loaded. Event not sent.');
    return;
  }

  const typeKey = String(newType || '').trim().toUpperCase();
  const eventName = USER_TYPE_EVENT_MAP[typeKey];

  if (!eventName) {
    console.warn(`[CleverTap] Unknown User type: ${newType}`);
    return;
  }

  const eventData = buildVendorPayload(vendor, {
    previous_user_type: previousType || 'N/A',
    new_user_type: newType || 'N/A',
  });

  ct.event.push(eventName, eventData);
};
